import React, { useState } from "react";
import { useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";
import { Info, InfoOutlined } from "@mui/icons-material";
import { useHistory, useParams } from "react-router-dom";
import {
  UpfrontPaymentDesktopContainer,
  UpfrontPaymentFieldRowTitle,
  UpfrontPaymentInfoRow,
  UpfrontPaymentReceiptTitle,
  UpfrontPaymentDashed,
  UpfrontPaymentButtonRow,
  UpfrontPaymentFieldRow,
  UpfrontBillingCreateAccount,
  UpfrontBillingCreateAccountText,
  UpfrontBillingCreateAccountWrapper,
  UpfrontPaymentMainBanner,
  UpfrontPaymentMainBannerBottomRow,
  UpfrontPaymentMainBannerTopRow,
  UpfrontPaymentYouHaveABill,
  UpfrontPaymentHMOMedicaidWrapper,
} from "./UpfrontPayment.styles";

import { useDeviceHook } from "../../utils/hooks/useDeviceHook";

import { GetTypographyContent } from "../shared/Typography/Typography";
import { getUserAuth, getUserProfile } from "../../recoil/selectors";
import { Button } from "../shared/Button/Button";
import { UpfrontInsuranceUploadModal } from "./UpfrontPaymentInsuranceUploadModal";
import {
  PostUpfrontPaymentMedicaidHMODismiss,
  Bill,
} from "./Services/UpfrontPaymentService";
import { REGISTER_URL, LOGIN_PAGE_URL } from "../../utils/urlConstants";
import { InsuranceUploadType } from "../Insurance/types";

export const UpfrontPaymentInsurance: React.FC<{
  setUpfrontBill: React.Dispatch<React.SetStateAction<Bill | undefined>>;
  upfrontBill: Bill;
  setHomeScreen: () => void;
}> = ({ setUpfrontBill, upfrontBill, setHomeScreen }) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const history = useHistory();
  const profileInfo = useRecoilValue(getUserProfile);
  const [openSecondaryInsurance, setOpenSecondaryInsurance] =
    useState<boolean>(false);
  const [openMedicaidHmoUploadModal, setOpenMedicaidHmoUploadModal] =
    useState<boolean>(false);
  const auth = useRecoilValue(getUserAuth);
  const { adminID, billingPK } = useParams<{
    billingPK: string;
    adminID?: string;
  }>();
  return (
    <>
      {!auth?.access_token ? (
        <UpfrontPaymentDesktopContainer deviceType={deviceType}>
          <UpfrontBillingCreateAccountWrapper>
            <UpfrontBillingCreateAccount>
              <UpfrontBillingCreateAccountText>
                <Info />{" "}
                {upfrontBill.signup_url
                  ? t("upfrontPayment.insurance.createAccountPrompt")
                  : t("upfrontPayment.insurance.loginPrompt")}
              </UpfrontBillingCreateAccountText>
              <Button
                type="submit"
                label={
                  upfrontBill.signup_url
                    ? t("upfrontPayment.actions.createAccount")
                    : t("upfrontPayment.actions.login")
                }
                size="medium"
                onClick={() =>
                  upfrontBill.signup_url
                    ? history.push(
                        `${REGISTER_URL}${
                          upfrontBill.signup_url.split("c/s")[1]
                        }`,
                      )
                    : history.push(`${LOGIN_PAGE_URL}`)
                }
                backgroundColor="greenDark"
              />
            </UpfrontBillingCreateAccount>
          </UpfrontBillingCreateAccountWrapper>
        </UpfrontPaymentDesktopContainer>
      ) : (
        <>
          <UpfrontPaymentDesktopContainer deviceType={deviceType}>
            <UpfrontInsuranceUploadModal
              insuranceType={InsuranceUploadType.secondary}
              insuranceUploadModalOpen={openSecondaryInsurance}
              setInsuranceUploadModalOpen={setOpenSecondaryInsurance}
              setUpfrontBill={setUpfrontBill}
              setHomeScreen={setHomeScreen}
            />
            <UpfrontInsuranceUploadModal
              insuranceType={InsuranceUploadType.medicaid_or_hmo}
              insuranceUploadModalOpen={openMedicaidHmoUploadModal}
              setInsuranceUploadModalOpen={setOpenMedicaidHmoUploadModal}
              setUpfrontBill={setUpfrontBill}
              setHomeScreen={setHomeScreen}
            />
            <UpfrontPaymentReceiptTitle>
              {t("upfrontPayment.main.insurance")}
            </UpfrontPaymentReceiptTitle>
            <UpfrontPaymentFieldRow>
              <UpfrontPaymentFieldRowTitle>
                {t("upfrontPayment.insurance.companyName")}
              </UpfrontPaymentFieldRowTitle>
              {GetTypographyContent({
                content: profileInfo?.ins_provider || "-",
                size: "medium",
                textColor: "black",
              })}
            </UpfrontPaymentFieldRow>
            <UpfrontPaymentInfoRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.insurance.groupId")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content: profileInfo?.ins_group || "-",
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.insurance.memberId")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content: profileInfo?.ins_number || "-",
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
            </UpfrontPaymentInfoRow>
            <br />
            <UpfrontPaymentDashed />
            <br />
            <UpfrontPaymentReceiptTitle>
              {t("upfrontPayment.insurance.policyOwner")}
            </UpfrontPaymentReceiptTitle>
            <UpfrontPaymentFieldRow>
              <UpfrontPaymentFieldRowTitle>
                {t("upfrontPayment.insurance.relationshipToPolicyOwner")}
              </UpfrontPaymentFieldRowTitle>
              {GetTypographyContent({
                content: profileInfo?.relationship_to_insured || "-",
                size: "medium",
                textColor: "black",
              })}
            </UpfrontPaymentFieldRow>
            <UpfrontPaymentInfoRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.insurance.firstName")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content: profileInfo?.first_name || "-",
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.insurance.lastName")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content: profileInfo?.last_name || "-",
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
            </UpfrontPaymentInfoRow>
            <UpfrontPaymentInfoRow>
              <UpfrontPaymentFieldRow>
                <UpfrontPaymentFieldRowTitle>
                  {t("upfrontPayment.insurance.dateOfBirth")}
                </UpfrontPaymentFieldRowTitle>
                {GetTypographyContent({
                  content: profileInfo?.dob || "-",
                  size: "medium",
                  textColor: "black",
                })}
              </UpfrontPaymentFieldRow>
            </UpfrontPaymentInfoRow>
            {upfrontBill?.portal_bill_status !== "secondary_ins_provided" && (
              <UpfrontPaymentButtonRow tertiary>
                <Button
                  type="submit"
                  label={t("upfrontPayment.insurance.addSecondaryInsurance")}
                  size="medium"
                  reverse
                  onClick={() => setOpenSecondaryInsurance(true)}
                  backgroundColor="greyMedium"
                />
              </UpfrontPaymentButtonRow>
            )}
            {upfrontBill.show_medicaid_or_hmo_prompt && (
              <UpfrontPaymentHMOMedicaidWrapper>
                <UpfrontPaymentYouHaveABill>
                  <UpfrontPaymentMainBanner>
                    <UpfrontPaymentMainBannerTopRow>
                      <InfoOutlined />{" "}
                      {t("upfrontPayment.insurance.medicaidHmoQuestion")}
                    </UpfrontPaymentMainBannerTopRow>
                    <UpfrontPaymentMainBannerBottomRow>
                      <UpfrontPaymentButtonRow tertiary>
                        <Button
                          type="submit"
                          label={t("upfrontPayment.common.no")}
                          size="medium"
                          reverse
                          onClick={async () => {
                            const response =
                              await PostUpfrontPaymentMedicaidHMODismiss(
                                billingPK,
                                adminID,
                              );
                            if (response?.ok) {
                              const responseJson = await response.json();
                              setUpfrontBill(responseJson);
                            }
                          }}
                          backgroundColor="greyMedium"
                        />
                      </UpfrontPaymentButtonRow>
                      <UpfrontPaymentButtonRow tertiary>
                        <Button
                          type="submit"
                          label={t(
                            "upfrontPayment.insurance.uploadMedicaidHmo",
                          )}
                          size="medium"
                          reverse
                          onClick={() => setOpenMedicaidHmoUploadModal(true)}
                          backgroundColor="greyMedium"
                        />
                      </UpfrontPaymentButtonRow>
                    </UpfrontPaymentMainBannerBottomRow>
                  </UpfrontPaymentMainBanner>
                </UpfrontPaymentYouHaveABill>
              </UpfrontPaymentHMOMedicaidWrapper>
            )}
          </UpfrontPaymentDesktopContainer>
        </>
      )}
    </>
  );
};
